// src/pages/UseCaseProfilePage.jsx
// ============================================================
// CLIENT ID: AuthContext session.client_id is the PRIMARY source
// FIXED: Removed URL param priority - AuthContext is source of truth
// localStorage only used for admin client switching (secondary)
// All client IDs are STRING type for BigQuery compatibility
// ============================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Download, Share2, Star, TrendingUp, Users, DollarSign, Shield, Calendar, Target, Zap, AlertTriangle, CheckCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import * as apiService from "../services/apiService";
import HeaderOverview from '../components/profile/HeaderOverview';
import QuickJumpBar from '../components/profile/QuickJumpBar';
import SectionROI from '../components/profile/SectionROI';
import SectionFeasibility from '../components/profile/SectionFeasibility';
import SectionAlignment from '../components/profile/SectionAlignment';
import SectionRisks from '../components/profile/SectionRisks';
import SectionRoadmap from '../components/profile/SectionRoadmap';
import SectionRecommendation from '../components/profile/SectionRecommendation';
import SectionExecutive from '../components/profile/SectionExecutive';
import UseCaseActionsTab from '../components/profile/UseCaseActionsTab';
import ExecutiveOverview from '../components/profile/ExecutiveOverview/ExecutiveOverview';
import { mapExecutiveData } from '../components/profile/ExecutiveOverview/mapExecutiveData';
import { useRoiData } from '../hooks/useRoiData';
import { BaselinePill, SourceIndicator, ProfileSection } from '../components/customization/BaselinePill';

// Score display mode hook (unchanged)
const useScoreDisplay = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const displayModeFromUrl = searchParams.get('display');
  const [displayMode, setDisplayModeState] = useState(
    displayModeFromUrl || localStorage.getItem('scoreDisplayMode') || 'raw'
  );

  const updateDisplayMode = (mode) => {
    setDisplayModeState(mode);
    localStorage.setItem('scoreDisplayMode', mode);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('display', mode);
    setSearchParams(newParams);
  };

  useEffect(() => {
    if (displayModeFromUrl && displayModeFromUrl !== displayMode) {
      setDisplayModeState(displayModeFromUrl);
      localStorage.setItem('scoreDisplayMode', displayModeFromUrl);
    }
  }, [displayModeFromUrl]);

  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('scoreDisplayMode') || 'raw';
      if (stored !== displayMode) {
        setDisplayModeState(stored);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [displayMode]);

  return { displayMode, setDisplayMode: updateDisplayMode };
};

const UseCaseProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // ============================================================
  // CLIENT ID: AuthContext is the PRIMARY source
  // FIXED: Session is now the source of truth, not URL params
  // localStorage is secondary (for admin client switching only)
  // ============================================================
  const { session, sessionLoading } = useAuth();
  const sessionClientId = session?.client_id || null;
  
  // Priority: session (AuthContext) > localStorage (admin override)
  // REMOVED: URL param priority - AuthContext is source of truth
  const localStorageClientId = localStorage.getItem('dva_current_client');
  
  // Ensure all sources are strings (no parseInt!)
  const clientId = sessionClientId 
    ? String(sessionClientId) 
    : localStorageClientId 
      ? String(localStorageClientId) 
      : null;
  
  const lensFromUrl = searchParams.get('lens');
  const [lens, setLens] = useState(lensFromUrl || '70_30');
  const { displayMode, setDisplayMode } = useScoreDisplay();

  // Use ROI hook to get metrics - pass string clientId
  const { metrics: roiMetrics } = useRoiData(clientId, id ? String(id) : null);

  // NEW: Add syncing state to prevent premature renders
  const [isSyncing, setIsSyncing] = useState(true);

  // State variables (all hooks at top level)
  const [profileData, setProfileData] = useState(null);
  const [sections, setSections] = useState([]);
  const [rationalizations, setRationalizations] = useState([]);
  const [roiData, setRoiData] = useState(null);
  const [risksData, setRisksData] = useState([]);
  const [alignmentData, setAlignmentData] = useState(null);
  const [roadmapData, setRoadmapData] = useState({ milestones: [], requirements: [] });
  const [feasibilityData, setFeasibilityData] = useState(null);
  const [scores, setScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Lens update callback
  const updateLens = useCallback((newLens) => {
    setLens(newLens);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('lens', newLens);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Sync lens from URL
  useEffect(() => {
    if (lensFromUrl && lensFromUrl !== lens) {
      setLens(lensFromUrl);
    }
  }, [lensFromUrl]);

  // Initialize URL params (for display/lens only, not client)
  useEffect(() => {
    if (!clientId) return; // Don't update URL if no clientId yet
    
    const params = new URLSearchParams(searchParams);
    let updated = false;
    
    // Update URL to reflect current client (for sharing/bookmarking)
    if (params.get('client') !== clientId) {
      params.set('client', clientId);
      updated = true;
    }
    if (!params.get('lens')) {
      params.set('lens', lens);
      updated = true;
    }
    if (!params.get('display')) {
      params.set('display', displayMode);
      updated = true;
    }
    
    if (updated) {
      setSearchParams(params, { replace: true });
    }
  }, [clientId]);

  // Mark syncing as complete after session loads
  useEffect(() => {
    if (!sessionLoading) {
      // Brief delay to ensure state is settled
      setTimeout(() => {
        setIsSyncing(false);
      }, 50);
    }
  }, [sessionLoading]);

  // Listen for client changes from sidebar (admin feature)
  useEffect(() => {
    const handleClientChange = (event) => {
      const newClientId = String(event.detail.clientId); // Ensure string
      console.log('🔄 Client changed to:', newClientId);
      
      // Update URL params
      const newParams = new URLSearchParams(searchParams);
      newParams.set('client', newClientId);
      setSearchParams(newParams);
      
      // Reload the page to fetch new data
      window.location.reload();
    };

    window.addEventListener('clientChanged', handleClientChange);
    return () => window.removeEventListener('clientChanged', handleClientChange);
  }, [searchParams, setSearchParams]);

  // Fetch v2 detail - clientId is already a string
  const fetchDetailV2 = async (useCaseId, clientId) => {
    try {
      const res = await apiService.getUseCaseDetail(useCaseId, clientId);
      
      // Normalize naming from the working payload
      return {
        id: res.use_case_id || res.id,
        title: res.title || res.name,
        category: res.category || res.category_name,
        summary: res.summary || res.description || '',
        scores: {
          rank: res.rank ?? null,
          value: res.value_score ?? null,
          ease: res.implementation_score ?? null,
          total: res.total_score ?? null,
        },
        financial: res.financial_snapshot ?? null,
        raw: res
      };
    } catch (error) {
      console.error('Failed to fetch v2 detail:', error);
      throw error;
    }
  };

  // ============================================================
  // LOADING STATE: Wait for session
  // ============================================================
  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#18365E] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your session...</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // NO CLIENT ID STATE: Show error when no client available
  // ============================================================
  if (!clientId && !isSyncing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#18365E] mb-2">No Organization Selected</h2>
          <p className="text-gray-600 mb-6">
            Unable to load use case profile. Your account is not currently associated with an organization.
            Please complete onboarding or contact your administrator.
          </p>
          <button
            onClick={() => navigate('/home')}
            className="px-6 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#0F1E33] transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Main data fetch effect - wait for sync and clientId
  useEffect(() => {
    const fetchData = async () => {
      // Wait for clientId sync to complete
      if (!id || isSyncing || !clientId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log('📊 Fetching data for use case:', id, 'client:', clientId);
        
        // Fetch v2 detail (primary source for scores and financial)
        const detail = await fetchDetailV2(id, clientId);
        setProfileData(detail.raw);  // Keep full raw for subcomponents
        setScores(detail.scores);    // Reliable scores from detail
        
        // Map financial_snapshot to ROI data if present
        if (detail.financial) {
          const f = detail.financial;
          setRoiData({
            assumptions: [],
            calculations: [],
            sources: [],
            paybackMonths: f.payback_months ?? null,
            confidenceRollup: f.confidence ?? 'Medium',
            annualRevenuePotential: f.annual_revenue_potential ?? null,
            provenance: f.provenance ?? null,
            revenueBandLabel: f.revenue_band_label ?? null,
            explanation: f.explanation ?? null,
          });
        }
        
        // Optional subroutes (best-effort, fail gracefully)
        try {
          const sectionsResponse = await apiService.getUseCaseSections(id, clientId);
          setSections(Array.isArray(sectionsResponse) ? sectionsResponse : []);
        } catch (err) {
          console.warn('Sections not available:', err);
          setSections([]);
        }
        
        try {
          const rationalizationsResponse = await apiService.getUseCaseRationalizations(id);
          setRationalizations(Array.isArray(rationalizationsResponse) ? rationalizationsResponse : []);
        } catch (err) {
          console.warn('Rationalizations not available:', err);
          setRationalizations([]);
        }
        
        try {
          const risksResponse = await apiService.getUseCaseRisks(id, clientId);
          const risksArray = risksResponse?.risks || risksResponse || [];
          setRisksData(Array.isArray(risksArray) ? risksArray : []);
        } catch (err) {
          console.warn('Risks not available:', err);
          setRisksData([]);
        }
        
        try {
          const alignmentResponse = await apiService.getUseCaseAlignment(id, clientId);
          setAlignmentData(alignmentResponse);
        } catch (err) {
          console.warn('Alignment not available:', err);
          setAlignmentData(null);
        }
        
        try {
          const roadmapResponse = await apiService.getUseCaseRoadmap(id, clientId);
          const allMilestones = roadmapResponse?.phases?.reduce((acc, phase) => {
            return [...acc, ...phase.milestones];
          }, []) || [];
          setRoadmapData({
            milestones: allMilestones,
            totalDuration: roadmapResponse?.totalDuration || 12,
            phases: roadmapResponse?.phases || [],
            requirements: []
          });
        } catch (err) {
          console.warn('Roadmap not available:', err);
          setRoadmapData({ milestones: [], requirements: [] });
        }
        
        try {
          const feasibilityResponse = await apiService.getUseCaseFeasibility(id, clientId);
          setFeasibilityData(feasibilityResponse);
        } catch (err) {
          console.warn('Feasibility not available:', err);
          setFeasibilityData(null);
        }
        
      } catch (err) {
        console.error('Error fetching use case data:', err);
        setError('Failed to load use case profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, clientId, isSyncing]);

  // PDF download handler
  const handleDownloadPdf = useCallback(async () => {
    setDownloadingPdf(true);
    try {
      const url = await apiService.getArtifactUrl(id);
      if (url) {
        window.open(url, '_blank');
      } else {
        alert('PDF not available for this use case.');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [id]);

  // Executive data mapping
  const executiveData = useMemo(() => {
    if (!profileData) return null;
    
    const sectionMap = sections.reduce((acc, section) => {
      if (section.section_id) {
        acc[section.section_id] = section.section_name;
      }
      return acc;
    }, {});
    
    return mapExecutiveData(
      profileData,
      sections,
      sectionMap
    );
  }, [profileData, sections]);

  // Section configuration
  const sectionConfig = useMemo(() => [
    { key: 'overview', id: 'overview', label: 'Overview', icon: TrendingUp, color: 'blue' },
    { key: 'roi', id: 'roi', label: 'ROI', icon: DollarSign, color: 'green' },
    { key: 'feasibility', id: 'feasibility', label: 'Feasibility', icon: CheckCircle, color: 'purple' },
    { key: 'alignment', id: 'alignment', label: 'Alignment', icon: Target, color: 'orange' },
    { key: 'risks', id: 'risks', label: 'Risks', icon: Shield, color: 'red' },
    { key: 'roadmap', id: 'roadmap', label: 'Roadmap', icon: Calendar, color: 'indigo' },
    { key: 'recommendation', id: 'recommendation', label: 'Recommendation', icon: Zap, color: 'teal' }
  ], []);

  // Render section content
  const renderSection = (sectionKey) => {
    const commonProps = {
      profileData,
      sections,
      rationalizations,
      clientId,
      useCaseId: id,
      displayMode,
      lens
    };

    const currentSectionData = sections.find(s => 
      s.section_name?.toLowerCase().includes(sectionKey.replace('-', ' '))
    );

    switch(sectionKey) {
      case 'overview':
        return <ExecutiveOverview data={executiveData} />;

      case 'roi':
      case 'roi-details':
        return (
          <SectionROI
            clientId={clientId}
            useCaseId={id}
            profileData={profileData}
            roiData={roiData}
            section={currentSectionData}
          />
        );

      case 'feasibility':
        return (
          <SectionFeasibility 
            {...commonProps}
            id="feasibility"
            feasibilityData={feasibilityData}
            section={currentSectionData}
          />
        );
      
      case 'alignment':
      case 'strategic-alignment':
        return (
          <SectionAlignment 
            {...commonProps}
            id="strategic-alignment"
            alignmentData={alignmentData}
            section={currentSectionData}
          />
        );
      
      case 'risks':
      case 'risks-governance':
        return (
          <SectionRisks 
            {...commonProps}
            id="risks"
            risks={risksData}
            section={currentSectionData}
          />
        );
      
      case 'roadmap':
        return (
          <SectionRoadmap 
            {...commonProps}
            id="roadmap"
            milestones={roadmapData.milestones || []}
            requirements={roadmapData.requirements || []}
            isCustomized={roadmapData.isCustomized || false}
            section={currentSectionData}
          />
        );

      case 'actions':
        return (
          <UseCaseActionsTab
            clientId={clientId}
            useCaseId={id}
            profileData={profileData}
          />
        );

      case 'recommendation':
        return (
          <SectionRecommendation 
            {...commonProps}
            id="recommendations"
            section={currentSectionData}
          />
        );
      
      default:
        return (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-600">Section not found: {sectionKey}</p>
          </div>
        );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#18365E] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading use case profile...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <button 
            onClick={() => navigate('/use-cases')}
            className="mt-4 px-4 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#0F1E33]"
          >
            Back to Use Cases
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No profile data available for this use case.</p>
          <button 
            onClick={() => navigate('/use-cases')}
            className="mt-4 px-4 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#0F1E33]"
          >
            Back to Use Cases
          </button>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderOverview 
        profile={profileData}
        scores={scores}
        roiData={roiData}
        roiMetrics={roiMetrics}
        lens={lens}
        displayMode={displayMode}
        onBack={() => navigate('/use-cases')}
        onDownloadPdf={handleDownloadPdf}
        downloadingPdf={downloadingPdf}
      />

      {/* Quick Jump Bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3">
            <QuickJumpBar 
              sections={sectionConfig}
              activeSection={activeSection}
              onSectionChange={setActiveSection}
              useCaseId={id}
              clientId={clientId}
              lens={lens}
              displayMode={displayMode}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {renderSection(activeSection)}
        </div>
      </div>
    </div>
  );
};

export default UseCaseProfilePage;